import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { RotateCw, AlertTriangle, ShoppingBag, Clock, TrendingDown, Percent, Mail, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * RefundAnalyticsTab — admin view of refund/dispute health per seller.
 * Surfaces refund rate, dispute resolution time, open disputes, a 6-month
 * platform refund-rate trend, and a heuristic churn-risk score so the team
 * can proactively reach out to at-risk sellers.
 */
export default function RefundAnalyticsTab({ language }) {
  const isLV = language === 'lv';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [riskFilter, setRiskFilter] = useState('');
  const [emailing, setEmailing] = useState(null);

  const sendSupportEmail = async (seller) => {
    const note = window.prompt(
      isLV ? `Sūtīt atbalsta e-pastu pārdevējam "${seller.seller_name}"?\nNeobligāta personīga piezīme:`
           : `Send a support email to "${seller.seller_name}"?\nOptional personal note:`,
      '',
    );
    if (note === null) return; // cancelled
    setEmailing(seller.seller_id);
    try {
      const res = await axios.post(`${API}/admin/sellers/${seller.seller_id}/support-email`, { note });
      toast.success(res.data?.email_sent
        ? (isLV ? `E-pasts nosūtīts: ${res.data.to}` : `Email sent: ${res.data.to}`)
        : (isLV ? 'Paziņojums izveidots (e-pasts atspējots)' : 'Notification created (email disabled)'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isLV ? 'Neizdevās nosūtīt' : 'Failed to send'));
    } finally {
      setEmailing(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/refund-analytics`);
      setData(res.data);
    } catch (err) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const s = data?.summary;
  const sellers = (data?.sellers || []).filter((x) => !riskFilter || x.churn_risk === riskFilter);
  const trend = data?.monthly_trend || [];
  const maxRate = Math.max(0.01, ...trend.map((t) => t.refund_rate));

  const riskBadge = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-amber-100 text-amber-700',
    low: 'bg-emerald-100 text-emerald-700',
  };
  const riskLabel = (r) => (isLV
    ? { high: 'Augsts', medium: 'Vidējs', low: 'Zems' }[r]
    : { high: 'High', medium: 'Medium', low: 'Low' }[r]);
  const pct = (v) => `${((v || 0) * 100).toFixed(1)}%`;
  const hrs = (v) => (v == null ? '—' : (v >= 48 ? `${(v / 24).toFixed(1)}d` : `${v.toFixed(1)}h`));

  if (loading && !data) {
    return <div className="py-12 text-center text-[#5C5C5C]">{isLV ? 'Ielādē...' : 'Loading...'}</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-refund-analytics-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-outfit text-2xl font-bold text-[#121212]">
            {isLV ? 'Atmaksu analītika' : 'Refund analytics'}
          </h2>
          <p className="text-sm text-[#5C5C5C]">
            {isLV ? 'Atmaksu likme, strīdu risināšanas laiks un churn risks pa pārdevējiem.'
                  : 'Refund rate, dispute resolution time and churn risk per seller.'}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="h-9 gap-1.5" data-testid="refund-analytics-refresh">
          <RotateCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {isLV ? 'Atjaunot' : 'Refresh'}
        </Button>
      </div>

      {/* Summary cards */}
      {s && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="refund-summary">
          <SummaryCard icon={Percent} color="text-[#D97736]" bg="bg-[#D97736]/10"
            label={isLV ? 'Kopējā atmaksu likme' : 'Overall refund rate'} value={pct(s.overall_refund_rate)} />
          <SummaryCard icon={ShoppingBag} color="text-[#4A6B53]" bg="bg-[#4A6B53]/10"
            label={isLV ? 'Apmaksāti pasūtījumi' : 'Paid orders'} value={s.total_orders} />
          <SummaryCard icon={Clock} color="text-[#6366f1]" bg="bg-[#6366f1]/10"
            label={isLV ? 'Vidējais risināšanas laiks' : 'Avg resolution time'} value={hrs(s.avg_resolution_hours)} />
          <SummaryCard icon={AlertTriangle} color="text-red-600" bg="bg-red-50"
            label={isLV ? 'Augsta riska pārdevēji' : 'High-risk sellers'} value={s.high_risk_sellers} />
        </div>
      )}

      {/* 6-month trend */}
      <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5" data-testid="refund-trend">
        <div className="flex items-center gap-2 mb-4">
          <TrendingDown className="w-5 h-5 text-[#D97736]" />
          <h3 className="font-outfit text-lg font-semibold text-[#121212]">
            {isLV ? 'Atmaksu likme (pēdējie 6 mēneši)' : 'Refund rate (last 6 months)'}
          </h3>
        </div>
        <div className="flex items-end justify-between gap-2 h-32">
          {trend.map((t) => (
            <div key={t.month} className="flex-1 flex flex-col items-center justify-end gap-1.5" title={`${t.approved}/${t.orders}`}>
              <span className="text-[10px] font-medium text-[#121212]">{pct(t.refund_rate)}</span>
              <div
                className="w-full max-w-[44px] rounded-t-md bg-[#D97736] transition-all"
                style={{ height: `${Math.max(4, (t.refund_rate / maxRate) * 96)}px` }}
              />
              <span className="text-[10px] text-[#5C5C5C]">{t.month.slice(2)}</span>
              <span className="text-[9px] text-[#9C9C9C]">{t.orders}{isLV ? ' pas.' : ' ord.'}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Per-seller table */}
      <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="font-outfit text-lg font-semibold text-[#121212]">
            {isLV ? 'Pa pārdevējiem' : 'By seller'}
          </h3>
          <div className="flex gap-2">
            {[['', isLV ? 'Visi' : 'All'], ['high', riskLabel('high')], ['medium', riskLabel('medium')], ['low', riskLabel('low')]].map(([v, lab]) => (
              <button key={v || 'all'} onClick={() => setRiskFilter(v)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  riskFilter === v ? 'bg-[#D97736] text-white border-[#D97736]'
                                   : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#D97736]'}`}
                data-testid={`refund-risk-${v || 'all'}`}>
                {lab}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto border border-[#E5E4E0] rounded-xl">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E4E0] bg-[#FAFAFA] text-xs text-[#5C5C5C] uppercase">
                <th className="text-left px-3 py-2">{isLV ? 'Pārdevējs' : 'Seller'}</th>
                <th className="text-right px-3 py-2">{isLV ? 'Pasūt.' : 'Orders'}</th>
                <th className="text-right px-3 py-2">{isLV ? 'Strīdi' : 'Disputes'}</th>
                <th className="text-right px-3 py-2">{isLV ? 'Atmaksu likme' : 'Refund rate'}</th>
                <th className="text-right px-3 py-2">{isLV ? 'Risināšana' : 'Resolution'}</th>
                <th className="text-right px-3 py-2">{isLV ? 'Atver.' : 'Open'}</th>
                <th className="text-left px-3 py-2">{isLV ? 'Churn risks' : 'Churn risk'}</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {sellers.length ? sellers.map((x) => (
                <tr key={x.seller_id} className="border-b border-[#E5E4E0] last:border-0" data-testid={`refund-seller-${x.seller_id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-[#121212]">{x.seller_name}</div>
                    {x.last_order_days_ago != null && (
                      <div className="text-[10px] text-[#9C9C9C]">
                        {isLV ? `pēdējais pasūt. pirms ${x.last_order_days_ago}d` : `last order ${x.last_order_days_ago}d ago`}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">{x.orders}</td>
                  <td className="px-3 py-2 text-right">{x.disputes}</td>
                  <td className={`px-3 py-2 text-right font-medium ${x.refund_rate >= 0.2 ? 'text-red-600' : 'text-[#121212]'}`}>{pct(x.refund_rate)}</td>
                  <td className="px-3 py-2 text-right text-[#5C5C5C]">{hrs(x.avg_resolution_hours)}</td>
                  <td className="px-3 py-2 text-right">{x.open_disputes}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${riskBadge[x.churn_risk]}`}>{riskLabel(x.churn_risk)}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {x.churn_risk !== 'low' && x.seller_email && (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => sendSupportEmail(x)}
                        disabled={emailing === x.seller_id}
                        className="h-7 gap-1.5 text-xs border-[#D97736] text-[#D97736] hover:bg-[#FFF8F0]"
                        data-testid={`refund-support-email-${x.seller_id}`}
                      >
                        {emailing === x.seller_id
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <><Mail className="w-3 h-3" />{isLV ? 'Atbalsts' : 'Support'}</>}
                      </Button>
                    )}
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-[#5C5C5C]">
                  {isLV ? 'Nav datu' : 'No data'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, color, bg, label, value }) {
  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-4">
      <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-2`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div className="text-2xl font-bold text-[#121212]">{value}</div>
      <div className="text-xs text-[#5C5C5C]">{label}</div>
    </div>
  );
}
